// src/i18n/useLang.js
//
// App-wide language for field screens (technician + site engineer).
// ONE shared value for the whole app: the login-page toggle, the header
// toggle and every screen read the same store, so switching anywhere
// switches everywhere at once (D-307).
//
// Stored per phone in localStorage ("tech_lang"). Saving it to the user's
// Firestore profile is NOT done here (phase 2): a technician's users doc id
// is not their Auth uid and the rules (correctly) do not let a technician
// edit their own users doc, so the old best-effort write always failed.
//
// Default "hi" = Roman-script Hinglish — field technicians already see
// Hinglish by default since the July 2026 pilot; keeping it avoids
// silently switching them to English.
//
// Every visible text goes through t(key) from technicianStrings.js —
// never type Hinglish (or English) text directly into a field screen.

import { useSyncExternalStore, useCallback } from "react";
import { STRINGS } from "./technicianStrings";

const STORAGE_KEY = "tech_lang";
const DEFAULT_LANG = "hi";
export const SUPPORTED_LANGS = ["hi", "en"];

const listeners = new Set();

function readLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED_LANGS.includes(saved) ? saved : DEFAULT_LANG;
  } catch {
    // Private mode / blocked storage — still render, just use the default.
    return DEFAULT_LANG;
  }
}

function subscribe(callback) {
  listeners.add(callback);
  // Another tab of the app changed the language → follow it.
  const onStorage = (e) => { if (e.key === STORAGE_KEY) callback(); };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(callback);
    window.removeEventListener("storage", onStorage);
  };
}

// Change the language for the whole app (usable outside React too).
export function setAppLang(newLang) {
  if (!SUPPORTED_LANGS.includes(newLang)) return;
  try { localStorage.setItem(STORAGE_KEY, newLang); } catch { /* ignore */ }
  listeners.forEach((notify) => notify());
}

export function getAppLang() {
  return readLang();
}

// Look up a text. Falls back to English, then to the key itself, so a
// missing translation never shows a blank. {name}-style placeholders are
// filled from vars, e.g. t("welcome_name", { name: "kamal" }).
export function translate(key, lang, vars) {
  let text = STRINGS[key]?.[lang] ?? STRINGS[key]?.en ?? key;
  if (vars) {
    Object.keys(vars).forEach((k) => {
      text = text.split(`{${k}}`).join(String(vars[k]));
    });
  }
  return text;
}

// `_user` is ignored — kept so existing callers useLang(user) keep working.
export function useLang(_user) {
  const lang = useSyncExternalStore(subscribe, readLang, () => DEFAULT_LANG);
  const setLang = useCallback((newLang) => setAppLang(newLang), []);
  const t = useCallback((key, vars) => translate(key, lang, vars), [lang]);
  return { lang, setLang, t };
}